import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { VitalSignFragment$key } from "@/modules/patients/services/__generated__/VitalSignFragment.graphql"
import { VitalSignFragment } from "@/modules/patients/services/VitalSignFragment"

interface VitalSignsSummaryProps {
  vitalSignKey: VitalSignFragment$key
}

const VitalSignsSummary = ({ vitalSignKey }: VitalSignsSummaryProps) => {
  const { t } = useTranslation()
  const vitalSign = useFragment<VitalSignFragment$key>(VitalSignFragment, vitalSignKey)

  const entries: { label: string; value: string }[] = []

  if (vitalSign.oxygenSaturation !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.oxygenSaturation"),
      value: `${vitalSign.oxygenSaturation}%`,
    })
  }

  if (vitalSign.heartRate !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.pulse"),
      value: `${vitalSign.heartRate} bpm`,
    })
  }

  if (vitalSign.bloodPressureSystolic !== null && vitalSign.bloodPressureDiastolic !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.bloodPressure"),
      value: `${vitalSign.bloodPressureSystolic}/${vitalSign.bloodPressureDiastolic} mmHg`,
    })
  }

  if (vitalSign.breathRate !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.respiratoryRate"),
      value: `${vitalSign.breathRate} bpm`,
    })
  }

  if (vitalSign.bodyTemperature !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.bodyTemperature"),
      value: `${vitalSign.bodyTemperature} °C`,
    })
  }

  if (vitalSign.bloodSugarMmol !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.bloodSugar"),
      value: `${vitalSign.bloodSugarMmol} mmol/L`,
    })
  }

  if (vitalSign.bloodSugarMgDl !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.bloodSugar"),
      value: `${vitalSign.bloodSugarMgDl} mg/dL`,
    })
  }

  if (vitalSign.cReactiveProtein !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.cReactiveProtein"),
      value: `${vitalSign.cReactiveProtein} mg/L`,
    })
  }

  if (vitalSign.strepATestPositive !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.strepATest"),
      value: vitalSign.strepATestPositive
        ? t("episodeOfCare.progressNote.vitalSigns.strepAPositive")
        : t("episodeOfCare.progressNote.vitalSigns.strepANegative"),
    })
  }

  if (vitalSign.visualAnalogPainScale !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.visualAnalogPainScale"),
      value: `${vitalSign.visualAnalogPainScale}/10`,
    })
  }

  if (vitalSign.glasgowComaScaleEye !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.glasgowComaScaleEye"),
      value: `${vitalSign.glasgowComaScaleEye}/4`,
    })
  }

  if (vitalSign.glasgowComaScaleVerbal !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.glasgowComaScaleVerbal"),
      value: `${vitalSign.glasgowComaScaleVerbal}/5`,
    })
  }

  if (vitalSign.glasgowComaScaleMotor !== null) {
    entries.push({
      label: t("episodeOfCare.progressNote.vitalSigns.glasgowComaScaleMotor"),
      value: `${vitalSign.glasgowComaScaleMotor}/6`,
    })
  }

  if (entries.length === 0) {
    return null
  }

  return (
    <div className="bg-surface-subdued rounded-md p-3" data-testid="vital-signs-summary">
      <span className="text-on-surface-subdued mb-2 block text-xs font-medium">
        {t("episodeOfCare.progressNote.vitalSigns.title")}
      </span>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {entries.map((entry) => (
          <span key={entry.value} className="text-on-surface text-sm">
            <span className="text-on-surface-subdued">{entry.label}:</span> {entry.value}
          </span>
        ))}
      </div>
    </div>
  )
}

export { VitalSignsSummary }
